import React, { useEffect, useState, ReactNode } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { MessageCircle, Inbox, BookOpen, Users, UploadCloud, Store, GraduationCap, Building2, Ticket } from "lucide-react";
import { useSession } from "@/context/Session";
import { useApi } from "@/lib/api";
import { UiAssistant } from "@/components/UiAssistant";
import { LanguageMenu } from "@/components/LanguageMenu";
import { OrgSwitcher } from "@/components/OrgSwitcher";
import { GuestBanner } from "@/components/GuestBanner";
import { t } from "@/i18n";

const TABS: [string, string, any][] = [["/chat", "Chat", MessageCircle], ["/review", "Review", Inbox]];

function useReviewCount(enabled: boolean) {
  const api = useApi();
  const { pathname } = useLocation();
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const load = () => api.get("/review").then((r) => setN(r.data.questions.length + r.data.flags.length + r.data.needs_clarification.length)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("smboi:review-changed", load);
    return () => { clearInterval(t); window.removeEventListener("smboi:review-changed", load); };
  }, [api, enabled, pathname]);
  return n;
}

export const reviewChanged = () => window.dispatchEvent(new Event("smboi:review-changed"));

const Badge = ({ n }: { n: number }) =>
  n > 0 ? <span className="min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-[var(--status-error)] text-white text-[0.65rem] font-bold grid place-items-center" data-testid="review-badge">{n}</span> : null;

const IconLink = ({ to, icon: Icon, label, className = "grid" }: { to: string; icon: any; label: string; className?: string }) => (
  <NavLink to={to} title={t(label)} className={({ isActive }) => `w-8 h-8 sm:w-10 sm:h-10 rounded-full ${className} place-items-center transition-colors ${isActive ? "bg-[var(--accent-subtle)] text-[var(--accent-primary)]" : "faint hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)]"}`} data-testid={`nav-${label.toLowerCase()}`}>
    <Icon size={19} />
  </NavLink>
);

export function AppShell({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const count = useReviewCount(me.is_maintainer);
  return (
    <div className="min-h-screen" data-org-theme={me.org?.slug === "krusty-krab-restaurant" ? "krusty" : undefined} style={me.org?.slug === "krusty-krab-restaurant" ? ({ "--krusty-bg": "url(/themes/krusty-krab-bg.webp)" } as React.CSSProperties) : undefined} data-testid="app-shell">
      {me.org?.slug === "krusty-krab-restaurant" && <div aria-hidden className="krusty-bubbles" data-testid="krusty-theme-bg" />}
      <header className="sticky top-0 z-40 bg-[var(--header-bg)] backdrop-blur-xl border-b border-[var(--border-color)]">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 h-14 flex items-center gap-2 sm:gap-3">
          <Link to="/chat" className="flex items-center gap-2 font-bold tracking-tight text-lg shrink-0" data-testid="nav-logo"><img src="/mascot.png" alt="" className="h-8 w-auto" />{me.org?.logo_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${me.org.logo_url}`} alt="" className="h-8 w-8 rounded-md object-contain bg-white" data-testid="nav-org-logo" />}<span className="hidden lg:inline">SMBOI</span></Link>
          <div className="hidden lg:block min-w-0 leading-tight border-l border-[var(--border-color)] pl-3" data-testid="nav-identity">
            <Link to="/profile" className="block hover:opacity-70 transition-opacity">
              <p className="text-sm font-semibold truncate md:max-w-[14rem]" data-testid="nav-nickname">{me.nickname}</p>
              {(me.orgs?.length || 0) < 2 && <p className="text-xs faint truncate md:max-w-[16rem]" data-testid="nav-org-name">{me.org?.name}{me.role_names?.length > 0 && <span data-testid="nav-role-names"> · {me.role_names.join(", ")}</span>}</p>}
            </Link>
            {(me.orgs?.length || 0) > 1 && <div className="flex items-center gap-1 text-xs faint"><OrgSwitcher />{me.role_names?.length > 0 && <span className="truncate max-w-[8rem] xl:max-w-[10rem] shrink-0" data-testid="nav-role-names">· {me.role_names.join(", ")}</span>}</div>}
          </div>
          <nav className="hidden md:flex mx-auto p-1 rounded-full bg-[var(--bg-surface-hover)]" data-testid="main-nav">
            {TABS.map(([to, label, Icon]) => (
              <NavLink key={to} to={to} className={({ isActive }) => `px-5 py-1.5 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition-colors ${isActive ? "bg-[var(--bg-surface)] shadow-sm text-[var(--text-primary)]" : "faint hover:text-[var(--text-primary)]"}`} data-testid={`nav-${label.toLowerCase()}`}>
                <Icon size={15} /> {t(label)} {label === "Review" && <Badge n={count} />}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto md:ml-0 flex items-center gap-0 sm:gap-1 shrink-0">
            <IconLink to="/library" icon={BookOpen} label="Library" />
            <IconLink to="/training" icon={GraduationCap} label="Training" />
            {me.org?.type === "property_management" && <IconLink to="/properties" icon={Building2} label="Properties" />}
            {me.org && me.org.type !== "property_management" && (me.role === "admin" || (me.access || []).includes("cashier")) && <IconLink to="/coupons" icon={Ticket} label="Coupons" />}
            <IconLink to="/capture" icon={UploadCloud} label="Capture" className="hidden sm:grid" />
            {me.is_maintainer && <IconLink to="/customers" icon={Store} label="Customers" />}
            {me.role === "admin" && <IconLink to="/team" icon={Users} label="Team" />}
            <UiAssistant />
            <LanguageMenu />
            <span className="ml-1" data-testid="user-button"><UserButton userProfileMode="navigation" userProfileUrl="/profile" /></span>
          </div>
        </div>
        <div className="lg:hidden flex items-center gap-1.5 px-4 pb-2 -mt-1 text-xs min-w-0" data-testid="nav-identity-mobile">
          <Link to="/profile" className="font-semibold truncate">{me.nickname}</Link><span className="faint">·</span>
          {(me.orgs?.length || 0) < 2 ? <span className="faint truncate">{me.org?.name}</span> : <OrgSwitcher testid="org-switcher-mobile" />}
          {me.role_names?.length > 0 && <span className="faint truncate" data-testid="nav-role-names-mobile">· {me.role_names.join(", ")}</span>}
        </div>

      </header>
      <GuestBanner />
      <main className="tabbar-pad">{children}</main>
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[var(--header-bg)] backdrop-blur-xl border-t border-[var(--border-color)] grid grid-cols-2 pb-[env(safe-area-inset-bottom)]" data-testid="mobile-tabbar">
        {TABS.map(([to, label, Icon]) => (
          <NavLink key={to} to={to} className={({ isActive }) => `h-16 flex flex-col items-center justify-center gap-0.5 text-xs font-semibold ${isActive ? "text-[var(--accent-primary)]" : "faint"}`} data-testid={`tab-${label.toLowerCase()}`}>
            <span className="relative"><Icon size={22} />{label === "Review" && count > 0 && <span className="absolute -top-1 -right-2"><Badge n={count} /></span>}</span>{t(label)}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
